import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ClientPortal from './components/client/ClientPortal';
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import AuthCallbackPage from './components/auth/AuthCallbackPage';
import LandingPage from './components/landing/LandingPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import { UserProfile } from './types';
import { isTOTPVerified } from './utils/totpSession';
import { useEnvironment } from './hooks/useEnvironment';
import { loadPmcExperience } from './api/pmcExperienceService';
import AttestationPage from './components/auth/AttestationPage';

// Component to validate the session before allowing dashboard access.
const DashboardWithTwoFACheck: React.FC = () => {
  const [checking, setChecking] = useState<boolean>(true);
  const [userStatus, setUserStatus] = useState<UserProfile | null>(null);
  const [authFailed, setAuthFailed] = useState<boolean>(false);

  // Validate the token and load the user profile.
  useEffect(() => {
    const checkSession = async () => {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setChecking(false);
        return;
      }

      try {
        const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
          ? 'http://localhost:8000/api/'
          : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

        const response = await fetch(`${base}users/profile`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUserStatus(userData);
          // Apply PMC white-label branding + resident-flow gates for this
          // account (no-op for accounts without a profile row).
          // Apply branding before first paint so nav/buttons don't flash default purple.
          await loadPmcExperience(userData?.accountId ?? userData?.account_id);
        } else if (response.status === 401) {
          // Token is invalid/expired - clear it and redirect to login
          console.log('Token invalid or expired, clearing auth state');
          localStorage.removeItem('authToken');
          localStorage.removeItem('totpVerified');
          localStorage.removeItem('totpVerifiedAt');
          localStorage.removeItem('totpSetupAt');
          sessionStorage.removeItem('sessionActive');
          setAuthFailed(true);
        } else {
          console.log('Profile fetch failed:', response.status);
        }
      } catch (error) {
        console.error('Error validating session:', error);
      } finally {
        setChecking(false);
      }
    };

    checkSession();
  }, []);

  if (checking) {
    return (
      <div className="flex items-center justify-center h-screen" role="status" aria-live="polite">
        <div className="text-lg text-gray-600">Checking authentication...</div>
      </div>
    );
  }

  // If auth failed (401), redirect to login
  if (authFailed) {
    return <Navigate to="/login" replace />;
  }

  // If no user status could be fetched (no token), redirect to login
  if (!userStatus) {
    return <Navigate to="/login" replace />;
  }

  // Require the email OTP to have been verified in this session.
  if (!isTOTPVerified()) {
    console.log('🔒 Dashboard: email OTP not verified this session, redirecting to login');
    return <Navigate to="/login" />;
  }

  // Create a minimal user object for ClientPortal compatibility
  const mockUser = {
    uid: userStatus?.id?.toString() || 'user',
    email: userStatus?.email || '',
    displayName: userStatus?.displayName || '',
    photoURL: userStatus?.photoURL || '',
  } as any;

  return <ClientPortal appData={{}} user={mockUser} />;
};

// Route guard for /login - redirects to dashboard if already fully authenticated
const LoginRouteGuard: React.FC = () => {
  const token = localStorage.getItem('authToken');
  const totpVerified = isTOTPVerified();

  if (token && totpVerified) {
    console.log('✅ Already authenticated, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
};

// Route guard for /signup.
//
// staging/prod are invitation-only: a bare /signup with no token is the
// open self-serve entry point and must be blocked. But /signup?invite=…
// is the legitimate invitation-acceptance flow that property managers
// email to new residents — SignupPage uses ?invite to resolve the
// invitation, prefill email/property, then call /api/invitation/consume
// after register. That URL must always go through.
//
// Rule: render <SignupPage /> when EITHER self-serve is allowed
// (dev/local) OR the URL carries an ?invite=… token. Otherwise redirect
// to /login. Wait for the env probe to load before deciding so we don't
// flash-redirect during the initial fetch.
const SignupRouteGuard: React.FC = () => {
  const { selfServeSignup, loaded } = useEnvironment();
  const hasInviteToken =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('invite');
  if (!loaded) return null; // brief flicker is better than a wrong redirect
  if (selfServeSignup || hasInviteToken) {
    return <SignupPage />;
  }
  return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  useEffect(() => {
    // Mark session as active (for tracking purposes)
    if (!sessionStorage.getItem('sessionActive')) {
      console.log('🔄 New browser session detected');
      sessionStorage.setItem('sessionActive', 'true');
    }
  }, []);

  return (
    <Router>
      <Routes>
        {/* Landing page route - accessible to everyone */}
        <Route path="/" element={<LandingPage />} />

        {/* Login route - redirect to dashboard if already authenticated and verified */}
        <Route path="/login" element={<LoginRouteGuard />} />

        {/* Signup route — gated on dev/local only; staging/prod redirect to /login. */}
        <Route path="/signup" element={<SignupRouteGuard />} />

        {/* Forgot Password route - always accessible */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Reset Password route - handles token from email */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Auth Callback route - handles email verification and other auth actions */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Doctor / provider attestation - public, OTP-gated (link from email).
            Only used when a PMC profile sets doctorAttestation.enabled. */}
        <Route path="/attest" element={<AttestationPage />} />

        {/* Dashboard route - DashboardWithTwoFACheck handles all auth states:
            no token, expired token, email OTP verification, etc. */}
        <Route path="/dashboard" element={<DashboardWithTwoFACheck />} />

        {/* Terms of Service and Privacy Policy - linked from login page */}
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Redirect any unknown routes to landing page */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
};

export default App;